import React, { useState, useEffect, useCallback } from 'react';
import { apiJson, apiPost, apiPut, apiDelete } from '../api';
import { colors, theme } from '../styles';
import LoginAssistant from './LoginAssistant';

const PLATFORMS = [
  { id: 1, name: 'instagram', label: 'Instagram' },
  { id: 2, name: 'tiktok', label: 'TikTok' },
];

export default function Accounts({ onSelectAccount }) {
  const [accounts, setAccounts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loginAccount, setLoginAccount] = useState(null);
  const [form, setForm] = useState({ platform_id: 1, username: '', display_name: '', proxy_url: '', login_user: '', login_pass: '' });

  const load = useCallback(async () => {
    const [accData, statusData] = await Promise.all([
      apiJson('/accounts'),
      apiJson('/status'),
    ]);
    if (accData) setAccounts(accData.accounts);
    if (statusData) {
      setStatuses(statusData.statuses);
      // Auto-show login assistant if any account needs manual login
      const needsLogin = statusData.statuses.find(s => s.manual_control && s.running);
      if (needsLogin && !loginAccount) {
        const acc = accData?.accounts?.find(a => a.id === needsLogin.account_id);
        if (acc) setLoginAccount(acc);
      }
    }
  }, [loginAccount]);

  useEffect(() => {
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, [load]);

  const addAccount = async () => {
    if (!form.username.trim()) return;
    await apiPost('/accounts', form);
    setForm({ platform_id: 1, username: '', display_name: '', proxy_url: '', login_user: '', login_pass: '' });
    setShowAdd(false);
    load();
  };

  const togglePause = async (id) => {
    await apiPost(`/accounts/${id}/pause`);
    load();
  };

  const startAccount = async (id) => {
    // Optimistically show as running immediately
    setStatuses(prev => [...prev.filter(s => s.account_id !== id), { account_id: id, running: true, current_action: 'Starting...' }]);
    await apiPost(`/accounts/${id}/start`);
    load();
  };

  const stopAccount = async (id) => {
    await apiPost(`/accounts/${id}/stop`);
    setStatuses(prev => prev.map(s => s.account_id === id ? { ...s, running: false, current_action: null } : s));
    load();
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Delete this account and all its data?')) return;
    await apiDelete(`/accounts/${id}`);
    load();
  };

  const resetFailsafe = async (id) => {
    await apiPost(`/accounts/${id}/reset-failsafe`);
    load();
  };

  const getStatus = (id) => statuses.find(s => s.account_id === id) || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>Accounts</h2>
        <button style={theme.btnPrimary} onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Cancel' : '+ Add Account'}
        </button>
      </div>

      {showAdd && (
        <div style={{ ...theme.card, marginBottom: 16 }}>
          <div style={theme.row}>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Platform</label>
              <select
                style={theme.select}
                value={form.platform_id}
                onChange={e => setForm({ ...form, platform_id: parseInt(e.target.value) })}
              >
                {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 2 }}>
              <label style={theme.label}>Username</label>
              <input
                style={theme.input}
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="username (no @)"
              />
            </div>
          </div>
          <div style={theme.row}>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Platform Login (email/username)</label>
              <input
                style={theme.input}
                value={form.login_user}
                onChange={e => setForm({ ...form, login_user: e.target.value })}
                placeholder="login email or username"
                autoComplete="off"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Platform Password</label>
              <input
                type="password"
                style={theme.input}
                value={form.login_pass}
                onChange={e => setForm({ ...form, login_pass: e.target.value })}
                placeholder="password (stored encrypted)"
                autoComplete="off"
              />
            </div>
          </div>
          <div style={theme.row}>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Display Name (optional)</label>
              <input
                style={theme.input}
                value={form.display_name}
                onChange={e => setForm({ ...form, display_name: e.target.value })}
                placeholder="My Account"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>Proxy URL (optional)</label>
              <input
                style={theme.input}
                value={form.proxy_url}
                onChange={e => setForm({ ...form, proxy_url: e.target.value })}
                placeholder="socks5://user:pass@host:port"
              />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={theme.btnSuccess} onClick={addAccount}>Add Account</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {accounts.map(acc => {
          const st = getStatus(acc.id);
          const isRunning = st.running || acc.running;
          const isPaused = acc.paused;
          const isContinuous = st.continuous;
          const circuitOpen = acc.circuit_state === 'open';
          const platformLabel = PLATFORMS.find(p => p.id === acc.platform_id)?.label || acc.platform_name;

          return (
            <div key={acc.id} style={{
              ...theme.card,
              display: 'flex', alignItems: 'center', gap: 12,
              opacity: isPaused ? 0.5 : 1,
              borderColor: circuitOpen ? colors.red : isRunning ? colors.green : colors.border,
            }}>
              {/* Status dot */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: circuitOpen ? colors.red : isRunning ? colors.green : isPaused ? colors.textDim : colors.textDimmer,
              }} />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    @{acc.username}
                  </span>
                  <span style={theme.badge(colors.accent)}>{platformLabel}</span>
                  {acc.has_credentials && <span style={theme.badge(colors.green)}>Creds</span>}
                  {!acc.has_credentials && <span style={theme.badge(colors.orange)}>No Creds</span>}
                  {isPaused && <span style={theme.badge(colors.orange)}>Paused</span>}
                  {circuitOpen && <span style={theme.badge(colors.red)}>Circuit Open</span>}
                  {isRunning && !isContinuous && <span style={theme.badge(colors.green)}>Running</span>}
                  {isRunning && isContinuous && <span style={theme.badge(colors.green)}>Continuous</span>}
                </div>
                {acc.display_name && (
                  <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>{acc.display_name}</div>
                )}
                {st.current_action && (
                  <div style={{ fontSize: 11, color: colors.accent, marginTop: 4 }}>{st.current_action}</div>
                )}
                {acc.proxy_url && (
                  <div style={{ fontSize: 11, color: colors.textDimmer, marginTop: 2 }}>
                    Proxy: {acc.proxy_url.replace(/\/\/.*@/, '//***@')}
                  </div>
                )}
              </div>

              {/* Stats */}
              {isRunning && (
                <div style={{ textAlign: 'center', marginRight: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{st.actions_completed || 0}</div>
                  <div style={{ fontSize: 10, color: colors.textDim }}>Actions</div>
                </div>
              )}

              {/* Controls */}
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {!isRunning ? (
                  <button
                    style={{ ...theme.btnSmall, background: colors.green, color: '#000', fontWeight: 600 }}
                    onClick={() => startAccount(acc.id)}
                    disabled={isPaused}
                    title={isPaused ? 'Unpause first' : 'Start session'}
                  >Start</button>
                ) : (
                  <button
                    style={{ ...theme.btnSmall, background: colors.red, color: '#fff', fontWeight: 600 }}
                    onClick={() => stopAccount(acc.id)}
                  >Stop</button>
                )}
                <button
                  style={{ ...theme.btnSmall, background: isPaused ? colors.green : colors.orange, color: '#000', fontWeight: 600 }}
                  onClick={() => togglePause(acc.id)}
                  title={isPaused ? 'Resume' : 'Pause'}
                >{isPaused ? 'Resume' : 'Pause'}</button>
                <button
                  style={{ ...theme.btnSmall, background: colors.accent, color: '#fff' }}
                  onClick={() => onSelectAccount(acc)}
                  title="Configure"
                >Config</button>
                {circuitOpen && (
                  <button
                    style={{ ...theme.btnSmall, background: colors.yellow, color: '#000', fontWeight: 600 }}
                    onClick={() => resetFailsafe(acc.id)}
                    title="Reset failsafe"
                  >Reset</button>
                )}
                <button
                  style={{ ...theme.btnSmall, background: colors.redDark, color: '#fff' }}
                  onClick={() => deleteAccount(acc.id)}
                  title="Delete"
                >X</button>
              </div>
            </div>
          );
        })}
        {accounts.length === 0 && (
          <div style={{ ...theme.card, textAlign: 'center', color: colors.textDim }}>
            No accounts configured. Add one to get started.
          </div>
        )}
      </div>

      {/* Login Assistant Modal */}
      {loginAccount && (
        <LoginAssistant
          account={loginAccount}
          onClose={(success) => {
            setLoginAccount(null);
            load();
          }}
        />
      )}
    </div>
  );
}
